import pdf from '@/sui/components/results/impl/pdf'
import results from '@/sui/data/results'
import stringify from 'csv-stringify'
import go from '@/gojs/go'
import {
  freezeText,
  incidenceBoxPlot,
  Labels
} from '@/sui/tools/chart/boxPlot'

function toggle (array, value) {
  const index = array.indexOf(value)
  if (index === -1) {
    array.push(value)
    return true
  }

  array.splice(index, 1)
  return false
}

function buildSelectorChoice (selected, name, tooltip, short, disabled) {
  return {
    label: name,
    tooltip: tooltip,
    shortLabel: short,
    disabled: disabled,
    type: selected ? 'warning' : 'default'
  }
}

function buildSelectorLine (title, choices, select, subtitle) {
  return {
    title: title,
    subtitle: subtitle,
    choices: choices,
    select: select
  }
}

function qualityToText (q) {
  if (q === 0) {
    return 'Valeur dans la norme'
  }

  if (q === 1) {
    return 'Valeur seule'
  }

  if (q === 2) {
    return 'Valeur «outlier»'
  }

  if (q === 3) {
    return 'Valeur peu vraisemblante'
  }

  if (q === 4) {
    return 'Valeur non comparée'
  }

  if (q === 5) {
    return 'Valeur partielle'
  }

  return ''
}

function getRegionalStatistics (s, r, e) {
  if (s == null) {
    return null
  }

  const key = r === '' ? e : `${r}.${e}`
  return s[key]
}

function exportCSV (vm) {
  const result = []
  let hasMoy = false
  for (let e in vm.incidences) {
    const i = vm.incidences[e]
    const split = e.split('.')
    hasMoy = hasMoy || i.mean >= 0

    result.push(
      {
        disc: split[1],
        spe: split[2],
        uf: split[5],
        inf: split[0],
        den: split[3],
        ent: split[4],
        inc: i.incidence,
        num: i.numerator,
        div: i.divisor,
        qua: qualityToText(i.quality),
        natm: i.mean
      }
    )
  }

  const col = [
    { key: 'disc', header: `Discipline` },
    { key: 'spe', header: `Spécialité` },
    { key: 'uf', header: `Uf` },
    { key: 'inf', header: `Type d'infection` },
    { key: 'den', header: `Dénominateur` },
    { key: 'ent', header: `Porte d'entrée` },
    { key: 'inc', header: `Taux d'incidence` },
    { key: 'num', header: `Nombre d'infections` },
    { key: 'div', header: `Valeur du dénominateur` }
  ]
  if (hasMoy) {
    col.push(
      { key: 'qua', header: `Qualité` },
      { key: 'natm', header: 'Moyenne nationale' }
    )
  }

  stringify(
    result,
    {
      header: true,
      bom: true,
      delimiter: ';',
      columns: col
    },

    function (err, output) {
      if (err != null) {
        vm.$notify({
          type: 'danger',
          title: 'Erreur !',
          content: `Impossible d'exporter les données: ${err}.`
        })
      } else {
        setTimeout(
          function () {
            vm.csv = {
              filename: `${vm.chartTitle} - INCIDENCES.csv`,
              blob: new Blob([output], { type: 'text/plain' })
            }
          }, 10)
      }
    })
}

export default {
  tools: {
    resetArray: function (array) {
      array.splice(0, array.length)
    },

    exportPDF: pdf.exportPDF,
    exportCSV: exportCSV
  },

  chart: {
    plugins: function (vm) {
      const list = [
        incidenceBoxPlot.plugins.drawTitle(),
        incidenceBoxPlot.plugins.outlierLimit()
      ]

      if (vm.graphIndicatorLimit != null) {
        list.push(
          incidenceBoxPlot.plugins.indicatorLimit(vm.graphIndicatorLimit)
        )
      }

      return list
    },

    titles: function (vm, sc) {
      const t = vm.graph.infectionType
      const d = vm.graph.denominator
      const u = vm.select.selection
      const p = vm.select.population
      const e = [sc.types[vm.establishmentType], ...vm.graph.etypes.values]
      const c = vm.graph.entrances.values

      let ct = vm.chartTitle
      let title = `Répartition des taux d'incidence des ${t.subtitle} `
      if (c.length === 1) {
        if (!c[0].info.chartTitleFormat) {
          title += `sur «${freezeText(c[0].info.title)}», `
        } else {
          title += freezeText(c[0].info.chartTitleFormat())
        }
      }

      const spe = freezeText(p.spe === '__all__' ? p.disc : p.spe)
      if (u.uf === '__all__') {
        title += `dans les services ${spe}`
      } else {
        ct += ` - UF «${freezeText(u.uf)}» (${spe})`
      }

      title += `, pour ${d.info.label}`

      if (e.length === 1) {
        const type = go.denom.isSubPopDenominator(d.code) ? 'TOUS' : vm.establishmentType
        const et = sc.types[type]
        if (et.graphTitle == null) {
          title += `, pour les établissements de type ${et.title}`
        } else {
          title += `, ${et.graphTitle}`
        }
      }

      let warning = null
      if (vm.year !== vm.chartYear) {
        warning = `Résultats provisoires - taux d'incidence ${vm.year} comparé à la population ${vm.chartYear}`
      }

      return {
        main: ct,
        sub: title,
        warning: warning
      }
    },

    config: function (vm, sc) {
      const datasets = [{
        data: [],
        values: [],
        stats: [],
        backgroundColor: [],
        borderColor: [],
        medianColor: [],
        showIncidence: true,
        showBoxPlot: true,
        showOutliers: true,
        showQuality: true,
        showIndicator: false
      }]
      const labels = new Labels()

      const d = vm.graph.denominator
      const t = vm.graph.infectionType
      const u = vm.select.selection
      const p = vm.select.population

      vm.graph.entrances.values.forEach(
        c => {
          const color = c.color
          const info = {
            dset: vm.referenceDataset,
            inf: t.code,
            disc: u.disc,
            spe: u.spe,
            den: d.code,
            ent: c.code,
            uf: u.uf
          }
          const key = results.getIncidenceKey(info)
          const value = vm.incidences[key]
          const statistics = vm.getStatistics(
            {
              dset: vm.referenceDataset,
              inf: t.code,
              disc: p.disc,
              spe: p.spe,
              den: d.code,
              ent: c.code
            }
          )
          const stat = getRegionalStatistics(
            statistics, vm.region, vm.establishmentType
          )

          incidenceBoxPlot.add(
            datasets[0], labels, value, stat, color, c.info.title, false
          )
        }
      )

      const mainColor = vm.graph.entrances.values[0].color
      const etypes = vm.graph.etypes.values
      if (etypes.length > 0) {
        const info = {
          dset: vm.referenceDataset,
          inf: t.code,
          disc: p.disc,
          spe: p.spe,
          den: d.code,
          ent: vm.graph.entrances.values[0].code
        }
        const statistics = vm.getStatistics(info)

        if (statistics != null) {
          labels.setLabel(
            0,
            sc.types[vm.establishmentType].title
          )

          const key = results.getIncidenceKey(
            {
              dset: vm.referenceDataset,
              inf: t.code,
              disc: u.disc,
              spe: u.spe,
              den: d.code,
              ent: vm.graph.entrances.values[0].code,
              uf: '__all__'
            }
          )
          etypes.forEach(
            e => {
              const value = vm.getIncidence(e, key)
              const stat = getRegionalStatistics(
                statistics, vm.region, e
              )
              const title = sc.types[e].title

              incidenceBoxPlot.add(
                datasets[0], labels, value, stat, mainColor, title, true
              )
            }
          )
        }
      }

      return {
        options: incidenceBoxPlot.options(
          {
            qualities: sc.qualities,
            showOutlierLimit: vm.showOutlierLimit,
            showIndicatorLimit: vm.graphIndicatorLimit != null && vm.showIndicatorLimit,
            showValues: vm.showValues,
            subtitleFontSize: 11
          },
          vm.plotTitles,
          mainColor,
          labels
        ),
        data: {
          datasets: datasets
        }
      }
    }
  },

  data: {
    entrances: function (vm) {
      return {
        values: [],

        toggle: function (e) {
          if (vm.graph.etypes.values.length > 0) {
            this.values = [e]
          } else {
            toggle(this.values, e)
          }
        }
      }
    },

    etypes: {
      values: [],

      toggle: function (e) {
        toggle(this.values, e)
      }
    },

    select: function (vm, sc) {
      return {
        show: false,
        list: [],
        pdfList: [],
        target: 'list',
        selection: null,
        population: null,
        listMode: false,
        callback: null,

        showModal: function (mode, callback) {
          if (mode === 'pdf') {
            this.target = 'pdfList'
            this.listMode = true
          } else if (mode === 'list') {
            this.target = 'list'
            this.listMode = true
          } else {
            this.listMode = false
            this.target = 'list'
          }
          this.callback = callback
          this.show = true
        },

        hide: function (msg) {
          this.show = false
          if (this.callback != null) {
            this.callback(msg)
          }
        },

        all: function () {
          this[this.target] = [...vm.currentUnitList]
        },

        none: function () {
          this[this.target] = []
        },

        selected: function (u) {
          if (this.listMode) {
            return this[this.target].indexOf(u) !== -1
          }
          return u === this.selection
        },

        toggle: function (u) {
          if (this.listMode) {
            toggle(this[this.target], u)
          } else {
            this.selection = u
            this.population = { disc: u.disc, spe: u.spe }
            this.show = false
          }
        },

        name: function (u) {
          if (u.uf !== '__all__') {
            return u.uf
          }

          return 'Tous les services'
        },

        selectedName: function () {
          const u = this.selection
          const p = this.population
          if (u == null || p == null) {
            return null
          }

          const uf = (u.uf !== '__all__')
          const target = p.spe === '__all__' ? p.disc : p.spe
          let result = target
          if (uf) {
            result += `, UF «${u.uf}»`
          }

          return result
        }
      }
    }
  },

  selectors: {
    specialities: function (vm, sc) {
      const result = []
      if (vm.currentUnitList == null) {
        return []
      }

      for (let i in vm.specialities) {
        const s = vm.specialities[i]
        const choices = []
        vm.currentUnitList.forEach(
          u => {
            if ((u.spe === '__all__' && u.disc === s.code) ||
                (u.spe === s.code && s.discipline != null)) {
              const selected = vm.select.selected(u)
              const choice = buildSelectorChoice(selected, vm.select.name(u))
              choice.toggle = () => { vm.select.toggle(u) }
              choice.priority = u.uf !== '__all__' ? 1 : (u.spe !== '__all__' ? 2 : 3)
              choices.push(choice)
            }
          }
        )
        if (choices.length === 0) {
          continue
        }
        choices.sort(
          (f, s) => {
            const score = s.priority - f.priority
            if (score !== 0) {
              return score
            }

            return f.label.localeCompare(s.label)
          }
        )
        const line = buildSelectorLine(
          s.code, choices, (i) => choices[i].toggle(), s.subtitle
        )
        line.col = 6
        result.push([line])
      }
      result.sort(
        (f, s) => {
          return s[0].choices.length - f[0].choices.length
        }
      )
      return result
    },

    array: {
      filters: function (vm, sc) {
        const choices = []
        sc.filters.forEach(
          (f, i) => {
            choices.push(
              {
                selected: vm.filter === f,
                label: f.name,
                select: () => vm.selectFilter(i)
              }
            )
          }
        )
        return choices
      },

      entrances: function (vm, sc) {
        const choices = []
        const type = vm.infectionType.code
        vm.scopes[type].forEach(
          s => {
            choices.push(
              {
                selected: vm.scope === s,
                label: s.name,
                select: () => { vm.scope = s }
              }
            )
          }
        )
        return choices
      },

      infectionTypes: function (vm, sc) {
        const choices = []
        sc.infectionTypes.forEach(
          (t, i) => {
            if (vm.results.unitList[t.code] != null) {
              const c = {
                selected: vm.infectionType === t,
                label: t.shortName,
                select: () => {
                  vm.infectionType = t
                  vm.scope = vm.scopes[t.code][0]
                  vm.resetDenominators(t.code)
                }
              }
              choices.push(c)
            }
          }
        )
        return choices
      },

      denominators: function (vm, sc, initialSelect) {
        const choices = []
        const sel = []
        const groups = {
          ga: {
            label: '100 nouveau-nés stratifiés par âge gestationnel',
            short: '100 nouveau-nés par âge gestationnel',
            choice: null,
            list: []
          },
          bw: {
            label: '100 nouveau-nés stratifiés par poids de naissance',
            short: '100 nouveau-nés par poids de naissance',
            choice: null,
            list: []
          },
          igs: {
            label: '100 patients stratifiés par score igsII (classes REZO)',
            short: '100 patients par igsII',
            choice: null,
            list: []
          }
        }

        vm.currentDenominators.forEach(
          (d, i) => {
            if (vm.unitLists[d.code] == null) {
              return
            }

            const selected = vm.denominators.indexOf(i) !== -1
            for (let k in groups) {
              if (!d.code.startsWith(k)) {
                continue
              }

              const g = groups[k]
              if (g.choice == null) {
                g.choice = {
                  selected: selected,
                  label: g.label,
                  short: g.short,
                  select: () => {
                    g.list.forEach(
                      i => toggle(vm.denominators, i)
                    )
                    vm.denominators.sort((a, b) => a - b)
                  }
                }
                if (selected) {
                  sel.push(choices.length)
                }
                choices.push(g.choice)
              }
              g.list.push(i)

              return
            }

            if (selected) {
              sel.push(choices.length)
            }

            choices.push(
              {
                selected: selected,
                label: d.info.label,
                short: d.info.short,
                initialSelect: initialSelect.indexOf(d.code) !== -1,
                select: () => {
                  if (toggle(vm.denominators, i)) {
                    vm.denominators.sort((a, b) => a - b)
                  }
                }
              }
            )
          }
        )

        const result = {
          choices: choices,
          label: null
        }
        if (sel.length === 0) {
          result.label = '(Merci de sélectionner au moins un dénominateur)'
        } else {
          result.label = choices[sel[0]].short
          if (sel.length > 1) {
            result.label += `, ${choices[sel[1]].short.substring(0, 10)}...`
          }
        }
        return result
      }
    },

    graph: {
      infectionTypes: function (vm, sc) {
        const choices = []
        sc.infectionTypes.forEach(
          (t, i) => {
            if (vm.results.unitList[t.code] != null) {
              const c = buildSelectorChoice(
                vm.graph.infectionType === t, t.name, t.tooltip
              )
              c.index = i
              choices.push(c)
            }
          }
        )
        const line = buildSelectorLine(
          `Type d'infection`,
          choices,
          (i) => {
            const type = sc.infectionTypes[choices[i].index]
            vm.graph.infectionType = type
          }
        )
        return [line]
      },

      denominators: function (vm, sc) {
        const t = vm.graph.infectionType
        const u = vm.select.selection
        if (u == null || t == null) {
          return []
        }
        const choices = []
        sc.denominators[t.code].forEach(
          (d) => {
            const units = vm.results.units[t.code][d.code]
            if (units != null &&
                units.findIndex(
                  c => c.uf === u.uf && c.spe === u.spe && c.disc === u.disc
                ) !== -1) {
              const choice = buildSelectorChoice(
                vm.graph.denominator === d, d.info.label
              )
              choice.select = () => { vm.graph.denominator = d }
              choices.push(choice)
            }
          }
        )
        const line = buildSelectorLine(
          'Dénominateur', choices,
          (i) => { choices[i].select() }
        )
        return [line]
      },

      entrances: function (vm, sc) {
        const t = vm.graph.infectionType
        const d = vm.graph.denominator
        const u = vm.select.selection
        if (t == null || d == null || u == null) {
          return []
        }

        const lines = []
        vm.scopes[t.code].forEach(
          s => {
            const choices = []
            s.entrances.forEach(
              (e, i) => {
                const index = vm.graph.entrances.values.findIndex(
                  d => d.code === e.code
                )
                const c = results.getIncidenceKey(
                  {
                    inf: t.code,
                    den: d.code,
                    ent: e.code,
                    disc: u.disc,
                    spe: u.spe,
                    uf: u.uf
                  }
                )
                if (vm.incidences[c] != null) {
                  let type = 'default'
                  if (index === 0) {
                    type = 'warning'
                  } else if (index !== -1) {
                    type = 'info'
                  }
                  choices.push(
                    {
                      label: e.info.title,
                      tooltip: e.info.tooltip,
                      type: type,
                      toggle: function () {
                        vm.graph.entrances.toggle(e)
                      }
                    }
                  )
                }
              }
            )
            if (choices.length > 0) {
              lines.push(
                buildSelectorLine(s.name, choices, (i) => { choices[i].toggle() })
              )
            }
          }
        )
        return lines
      },

      establishments: function (vm, sc) {
        const d = vm.graph.denominator
        const t = vm.graph.infectionType
        const p = vm.select.population
        const e = vm.graph.entrances.values

        if (d == null || t == null || p == null || e == null || e.length === 0) {
          return []
        }
        const s = vm.getStatistics(
          {
            dset: vm.referenceDataset,
            inf: t.code,
            disc: p.disc,
            spe: p.spe,
            den: d.code,
            ent: e[0].code
          }
        )
        if (s == null) {
          return []
        }

        const et = go.denom.isSubPopDenominator(d.code) ? 'TOUS' : vm.establishmentType
        const choices = [
          {
            type: 'warning',
            active: true,
            label: sc.types[et].title
          }
        ]
        for (let k in sc.types) {
          if (k !== et && s[k] != null) {
            const selected = vm.graph.etypes.values.indexOf(k) !== -1
            const t = sc.types[k]
            choices.push(
              {
                label: t.title,
                disabled: e.length > 1,
                type: selected ? 'info' : 'default',
                toggle: function () {
                  vm.graph.etypes.toggle(k)
                }
              }
            )
          }
        }

        const lines = buildSelectorLine(
          `Types d'établissements`, choices,
          (i) => { if (i !== 0) { choices[i].toggle() } }
        )
        return [lines]
      },

      population: function (vm, sc) {
        if (vm.graph.infectionType == null) {
          return []
        }

        const name = vm.select.selectedName()
        const selected = name != null
        const choices = [
          buildSelectorChoice(
            selected, selected ? name : 'Sélectionner',
            'Cliquer pour sélectionner une discipline ou une UF'
          )
        ]
        return [
          buildSelectorLine(
            'Discipline ou UF', choices, () => vm.select.showModal()
          )
        ]
      },

      options: function (vm, sc) {
        const e = vm.graph.entrances.values
        if (!vm.showValuesToggle || e == null || e.length === 0) {
          return []
        }
        const list = [
          { l: `Taux d'incidence`, f: 'showValues' },
          { l: 'Limite outlier', f: 'showOutlierLimit' }
        ]

        if (vm.graphIndicatorLimit != null) {
          list.push(
            { l: 'Limite indicateur', f: 'showIndicatorLimit' }
          )
        }

        const choices = []
        list.forEach(
          l => {
            choices.push(
              {
                label: l.l,
                toggle: function () {
                  vm[l.f] = !vm[l.f]
                },
                type: vm[l.f] ? 'info' : 'default',
                active: vm[l.f]
              }
            )
          }
        )

        return [
          buildSelectorLine(
            'Options', choices, (i) => { choices[i].toggle() }
          )
        ]
      }
    }
  }
}
